import { supabase } from '../lib/supabase';

export interface Supplier {
  id: string;
  name: string;
  logoUrl: string;
  bannerUrl: string;
  isVerified: boolean;
  rating: number;
  enviosCount: number;
  produtosCount: number;
  descriptionShort: string;
  descriptionFull: string;
  redirectUrl: string;
  hospedarPassos: string;
  categories: string[];
  has_private_label: boolean;
  tempo_medio_entrega: string;
  whatsapp: string;
  user_id?: string | null;
}

export const mapDbToSupplier = (db: any): Supplier => ({
  id: db.id,
  name: db.name,
  logoUrl: db.logo_url,
  bannerUrl: db.banner_url,
  isVerified: db.is_verified,
  rating: Number(db.rating),
  enviosCount: db.envios_count,
  produtosCount: db.produtos_count,
  descriptionShort: db.description_short,
  descriptionFull: db.description_full,
  redirectUrl: db.redirect_url,
  hospedarPassos: db.hospedar_passos,
  categories: db.categories || [],
  has_private_label: db.has_private_label,
  tempo_medio_entrega: db.tempo_medio_entrega || '',
  whatsapp: db.whatsapp || '',
  user_id: db.user_id,
});

export const loadSuppliers = async (): Promise<Supplier[]> => {
  try {
    const { data, error } = await supabase
      .from('suppliers')
      .select('*')
      .order('created_at', { ascending: true });

    if (error) throw error;
    return (data || []).map(mapDbToSupplier);
  } catch (error) {
    console.error('Erro ao carregar fornecedores do Supabase:', error);
    return [];
  }
};

export const createSupplier = async (
  supplierData: Omit<Supplier, 'id'>
): Promise<Supplier | null> => {
  try {
    const payload = {
      name: supplierData.name,
      logo_url: supplierData.logoUrl,
      banner_url: supplierData.bannerUrl,
      is_verified: supplierData.isVerified,
      rating: supplierData.rating,
      envios_count: supplierData.enviosCount,
      produtos_count: supplierData.produtosCount,
      description_short: supplierData.descriptionShort,
      description_full: supplierData.descriptionFull,
      redirect_url: supplierData.redirectUrl,
      hospedar_passos: supplierData.hospedarPassos,
      categories: supplierData.categories,
      has_private_label: supplierData.has_private_label,
      tempo_medio_entrega: supplierData.tempo_medio_entrega,
      whatsapp: supplierData.whatsapp,
      user_id: supplierData.user_id,
    };

    const { data, error } = await supabase
      .from('suppliers')
      .insert(payload)
      .select()
      .single();

    if (error) throw error;
    return mapDbToSupplier(data);
  } catch (error) {
    console.error('Erro ao criar fornecedor no Supabase:', error);
    return null;
  }
};

export const updateSupplier = async (
  id: string,
  supplierData: Partial<Supplier>
): Promise<Supplier | null> => {
  try {
    const payload: any = {};
    if (supplierData.name !== undefined) payload.name = supplierData.name;
    if (supplierData.logoUrl !== undefined) payload.logo_url = supplierData.logoUrl;
    if (supplierData.bannerUrl !== undefined) payload.banner_url = supplierData.bannerUrl;
    if (supplierData.isVerified !== undefined) payload.is_verified = supplierData.isVerified;
    if (supplierData.rating !== undefined) payload.rating = supplierData.rating;
    if (supplierData.enviosCount !== undefined) payload.envios_count = supplierData.enviosCount;
    if (supplierData.produtosCount !== undefined) payload.produtos_count = supplierData.produtosCount;
    if (supplierData.descriptionShort !== undefined) payload.description_short = supplierData.descriptionShort;
    if (supplierData.descriptionFull !== undefined) payload.description_full = supplierData.descriptionFull;
    if (supplierData.redirectUrl !== undefined) payload.redirect_url = supplierData.redirectUrl;
    if (supplierData.hospedarPassos !== undefined) payload.hospedar_passos = supplierData.hospedarPassos;
    if (supplierData.categories !== undefined) payload.categories = supplierData.categories;
    if (supplierData.has_private_label !== undefined) payload.has_private_label = supplierData.has_private_label;
    if (supplierData.tempo_medio_entrega !== undefined) payload.tempo_medio_entrega = supplierData.tempo_medio_entrega;
    if (supplierData.whatsapp !== undefined) payload.whatsapp = supplierData.whatsapp;
    if (supplierData.user_id !== undefined) payload.user_id = supplierData.user_id;

    payload.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('suppliers')
      .update(payload)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return mapDbToSupplier(data);
  } catch (error) {
    console.error('Erro ao atualizar fornecedor no Supabase:', error);
    return null;
  }
};

export const deleteSupplier = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('suppliers')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Erro ao excluir fornecedor do Supabase:', error);
    return false;
  }
};

export const SUPPLIER_CATEGORIES = [
  'Saúde, Beleza e Cuidados Pessoais',
  'Casa, Móveis e Cozinha',
  'Tecnologia e Eletrodomésticos',
  'Moda, Roupas e Acessórios',
  'Relógios e Joias',
  'Animais e Pets',
  'Bebês, Jogos e Brinquedos',
  'Esportes e Fitness',
  'Veículos',
  'Livraria e Papelaria',
  'Ferramentas',
  'Outros'
];
